'use client';

import React, { useState, useEffect } from 'react';
import FilterBar from './FilterBar';
import VideoModal from './VideoModal';
import ImpactMetrics from './ImpactMetrics';
import CTASection from './CTASection';
import ESGChatbot from './ESGChatbot';

interface MapLocation {
  id: number;
  name: string;
  region: string;
  lat: number;
  lng: number;
  projects: number;
  co2Sequestered: string;
}

interface ClientServed {
  id: number;
  name: string;
  services: string[];
}

interface ImpactArea {
  id: number;
  title: string;
  description: string;
  icon: string;
}

const ImpactStoriesInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mapLocations: MapLocation[] = [
    {
      id: 1,
      name: 'India Operations',
      region: 'India',
      lat: 20.5937,
      lng: 78.9629,
      projects: 104,
      co2Sequestered: '2.02M tons'
    },
    {
      id: 2,
      name: 'UAE Operations',
      region: 'UAE',
      lat: 24.4539,
      lng: 54.3773,
      projects: 33,
      co2Sequestered: '670K tons'
    },
    {
      id: 3,
      name: 'Global Projects',
      region: 'International',
      lat: 40.7128,
      lng: -74.0060,
      projects: 47,
      co2Sequestered: '890K tons'
    }
  ];

  const clientsServed: ClientServed[] = [
    {
      id: 1,
      name: 'Karnataka Bank',
      services: ['BRSR Reporting', 'FIBAC Reporting', 'NetZero Roadmap']
    },
    {
      id: 2,
      name: 'Heritage Foods',
      services: ['Sustainable Food Operations']
    },
    {
      id: 3,
      name: 'RPG Biochar',
      services: ['Carbon Credit Development']
    },
    {
      id: 4,
      name: 'Makhana Sustainable Food & Cosmetics',
      services: ['Export/Import Operations', 'Sustainability Consulting']
    },
    {
      id: 5,
      name: 'Pharma Company',
      services: ['ESG Scope 1, 2, 3', 'Sustainability Reporting']
    },
    {
      id: 6,
      name: 'Value Chain Mining Company',
      services: ['Processing Operations', 'Environmental Impact Assessment']
    },
    {
      id: 7,
      name: 'Solar Power Transmission',
      services: ['Carbon Credit Generation']
    },
    {
      id: 8,
      name: 'Municipal Lake Conservation',
      services: ['Collaboration in Madhya Pradesh']
    },
    {
      id: 9,
      name: 'STP Water Recycling',
      services: ['Blue Credits - AP/TG']
    },
    {
      id: 10,
      name: 'Marsh Land Rejuvenation',
      services: ['Finland Project']
    },
    {
      id: 11,
      name: 'Value Chain Partners',
      services: ['CSRD Certification', 'Software Services', 'BRSR Lite Filing']
    },
    {
      id: 12,
      name: 'Data Center',
      services: ['Carbon Footprint Reduction']
    }
  ];

  const impactAreas: ImpactArea[] = [
    {
      id: 1,
      title: 'Trusted ESG Partner and Board Committee Members',
      description: 'Most trusted ESG company for environment reporting, BRSR compliance for Top 100 clients, providing comprehensive board-level advisory and strategic sustainability governance.',
      icon: '🏛️'
    },
    {
      id: 2,
      title: 'BRSR Lite and International GHG Reporting',
      description: 'Comprehensive reporting solutions for value chain partners, enabling transparent sustainability disclosure and compliance with international GHG standards across global operations.',
      icon: '📊'
    },
    {
      id: 3,
      title: 'Industry Emission Reporting & Net Zero Roadmap',
      description: 'Advanced emission tracking and reporting systems, low emission strategies, and Net Zero roadmap creation for enterprises across sectors with data-driven decarbonization pathways.',
      icon: '🏭'
    },
    {
      id: 4,
      title: 'Smart Farming & Desert Rejuvenation',
      description: 'Digital and IoT/AI strategies for sustainable agriculture, desert reclamation, and soil health restoration programs leveraging cutting-edge technology for environmental regeneration.',
      icon: '🌾'
    },
    {
      id: 5,
      title: 'Water Recycling & Blue Credits',
      description: 'Water recycling, STP optimization, large water body conservation, and sustainable modern fisheries export enhancement with AI & IoT for brackish water prawns and aquaculture.',
      icon: '💧'
    },
    {
      id: 6,
      title: 'Sustainable Supply Chain & EHS SAP',
      description: 'Comprehensive supply chain sustainability covering transportation, power, oil upstream emission monitoring, and EHS SAP expertise for integrated environmental management systems.',
      icon: '🚛'
    },
    {
      id: 7,
      title: 'New Sustainable Energy with AI & Quantum IP',
      description: 'Solar, wind, and hydrogen energy creation with AMC support, leveraging AI and quantum technologies as intellectual property for next-generation sustainable power infrastructure.',
      icon: '⚡'
    },
    {
      id: 8,
      title: 'Carbon Credit Algorithms & Registration',
      description: 'Complete carbon credit lifecycle management including study, registration, and earnings acceleration for public government initiatives, private enterprises, land and plantation owners.',
      icon: '🌳'
    },
    {
      id: 9,
      title: 'Global Ecosystem Restoration',
      description: 'Seacoast, land, and water body rejuvenation including Africa desert restoration, Malaysia reforestation, Finland marsh land restoration, German Rhine Valley restoration, France power energy, oil fracking environment restoration, and port development marine restoration using mangrove eco-relocation.',
      icon: '🌍'
    },
    {
      id: 10,
      title: 'Railway Sustainability - Japan',
      description: 'Comprehensive sustainability solutions for Japanese railway systems including energy efficiency optimization, green infrastructure development, carbon footprint reduction strategies, and integration of renewable energy sources for zero-emission rail operations.',
      icon: '🚄'
    },
    {
      id: 11,
      title: 'Railway Sustainability - Germany',
      description: 'Advanced environmental consulting for German railway networks focusing on emission monitoring, sustainable rail infrastructure, energy management systems, and implementation of climate-neutral transportation technologies across Deutsche Bahn operations.',
      icon: '🚆'
    },
    {
      id: 12,
      title: 'Railway Sustainability - Switzerland',
      description: 'Precision sustainability engineering for Swiss railway systems covering alpine rail infrastructure, hydroelectric-powered trains, ecosystem preservation along rail corridors, and biodiversity protection in mountainous railway environments.',
      icon: '🚞'
    },
    {
      id: 13,
      title: 'Airport Sustainability & Carbon Neutrality',
      description: 'Comprehensive airport decarbonization programs including terminal energy optimization, sustainable aviation fuel integration, ground operations emission reduction, carbon offset strategies, and achievement of Airport Carbon Accreditation for international aviation hubs.',
      icon: '✈️'
    },
    {
      id: 14,
      title: 'Port Ecology Restitution',
      description: 'Marine ecosystem restoration and port environmental rehabilitation including coastal habitat regeneration, seawater quality management, sustainable port operations, marine biodiversity enhancement, and blue carbon credit development for port authorities and shipping logistics centers.',
      icon: '⚓'
    }
  ];

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-64 bg-surface rounded-lg" />
            <div className="h-32 bg-surface rounded-lg" />
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-96 bg-surface rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <ImpactMetrics className="mb-12" />

        <FilterBar
          selectedRegion={selectedRegion}
          selectedIndustry=""
          onRegionChange={setSelectedRegion}
          onIndustryChange={() => {}}
          className="mb-8"
        />

        {/* Clients Served Section */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-4xl text-text-primary mb-4">
              Clients Served
            </h2>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">
              Trusted partnerships across industries, driving sustainable transformation and environmental excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientsServed.map((client) => (
              <div
                key={client.id}
                className="bg-surface border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 hover:border-accent-green"
              >
                <h3 className="font-heading font-bold text-xl text-text-primary mb-4">
                  {client.name}
                </h3>
                <div className="space-y-2">
                  {client.services.map((service, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-text-secondary"
                    >
                      <div className="w-2 h-2 bg-accent-green rounded-full flex-shrink-0" />
                      <span className="text-sm">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Impact Areas Section */}
        <div className="mb-16">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-4xl text-text-primary mb-4">
              Impact Areas
            </h2>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">
              Comprehensive ESG and sustainability solutions spanning emission reduction, conservation, carbon credits, and global ecosystem restoration initiatives
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {impactAreas.map((area) => (
              <div
                key={area.id}
                className="bg-surface border border-border rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:border-accent-green group"
              >
                <div className="flex items-start gap-4">
                  <div className="text-5xl flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    {area.icon}
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl text-text-primary mb-3">
                      {area.title}
                    </h3>
                    <p className="text-text-secondary leading-relaxed">
                      {area.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* GenCarbon Office Details Section */}
        <div className="mb-16 bg-gradient-to-r from-accent-green/10 to-accent-green/5 border border-accent-green/20 rounded-xl p-10">
          <div className="text-center mb-8">
            <h2 className="font-heading font-bold text-4xl text-text-primary mb-4">
              GenCarbon Offices
            </h2>
            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
              Visit our offices to discuss your sustainability goals and carbon neutrality strategies
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-surface border border-border rounded-xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-4xl">🏢</div>
                <h3 className="font-heading font-bold text-2xl text-text-primary">
                  Headquarters
                </h3>
              </div>
              <div className="space-y-3 text-text-secondary">
                <div className="flex items-start gap-2">
                  <span className="text-accent-green mt-1">📍</span>
                  <div>
                    <p className="font-semibold text-text-primary">Rajapushpa Summit, WeWork</p>
                    <p>Gachibowli, Hyderabad 500075</p>
                    <p>Telangana, India</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-8 hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-4xl">🏢</div>
                <h3 className="font-heading font-bold text-2xl text-text-primary">
                  Branch Office
                </h3>
              </div>
              <div className="space-y-3 text-text-secondary">
                <div className="flex items-start gap-2">
                  <span className="text-accent-green mt-1">📍</span>
                  <div>
                    <p className="font-semibold text-text-primary">6A Ecoworld, CoWorks</p>
                    <p>Bellandur, Bangalore 560103</p>
                    <p>Karnataka, India</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-text-secondary mb-4">Schedule a consultation at any of our locations</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="https://wa.me/919876543210"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-[#25D366] text-white rounded-lg hover:bg-[#20BA5A] transition-colors duration-300 font-medium flex items-center gap-2"
              >
                <span>💬</span>
                WhatsApp
              </a>
              <a
                href="mailto:info@gencarbon.com"
                className="px-6 py-3 bg-accent-green text-white rounded-lg hover:bg-accent-green/90 transition-colors duration-300 font-medium flex items-center gap-2"
              >
                <span>📧</span>
                Email Us
              </a>
            </div>
          </div>
        </div>

        <CTASection />

        {/* ESG Chatbot */}
        <ESGChatbot />
      </div>

      <VideoModal
        isOpen={isVideoModalOpen}
        videoUrl={currentVideoUrl}
        onClose={() => setIsVideoModalOpen(false)}
      />
    </>
  );
};

export default ImpactStoriesInteractive;